import React, { useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTheme } from '@mui/material/styles';

import { getNotifications, setNotificationsPage, setNotificationsPageItems } from '../../../store/messaging/actions';
// components
import Widget from '../../../components/Widget/Widget';
import Table from '../../../components/Table';
import { ROUTES, NAMES, IDS } from '../../../constants/routes';
import Breadcrumbs from '../../../components/Breadcrumbs';

const breadcrumsRoutes = [
  {
    route: ROUTES[IDS.MESSAGING],
    name: NAMES[IDS.MESSAGING],
  },
];

const headCells = [
  { id: 'title', numeric: false, disablePadding: true, label: 'Title' },
  { id: 'body', numeric: false, disablePadding: false, label: 'Body' },
  { id: 'type', numeric: false, disablePadding: false, label: 'Type' },
  { id: 'fcmToken', numeric: false, disablePadding: false, label: 'FCM Token' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
  { id: 'createdAt', numeric: false, disablePadding: false, label: 'Created At' },
  { id: 'updatedAt', numeric: false, disablePadding: false, label: 'Updated At' },
];

const Notifications = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const classes = useStyles();

  // global
  const dispatch = useDispatch();
  const selectedProjectId = useSelector((state) => state.projects.selectedProjectId);
  const isPending = useSelector((state) => state.messaging.notifications.pending);
  const data = useSelector((state) => state.messaging.notifications.data);
  const count = useSelector((state) => state.messaging.notifications.count);
  const pageNumber = useSelector((state) => state.messaging.notifications.pageNumber);
  const pageItemCount = useSelector((state) => state.messaging.notifications.pageItemCount);

  const tableData = useMemo(
    () =>
      data.reduce((acc, i) => {
        const d = _.pick({ ...i, type: i.type || '-' }, ['title', 'body', 'type', 'fcmToken', 'status', 'createdAt', 'updatedAt']);

        if (d.title.length > 20) {
          d.title = `${d.title.slice(0, 20)}...`;
        }

        if (d.body.length > 20) {
          d.body = `${d.body.slice(0, 20)}...`;
        }
        d.fcmToken = `${d.fcmToken.slice(0, 20)}...`;
        return [...acc, d];
      }, []),
    [data],
  );

  // Another project restarts the list: page 5 of this one may not exist there.
  useEffect(() => {
    dispatch(setNotificationsPage(1));
  }, [selectedProjectId]);

  useEffect(() => {
    dispatch(getNotifications());
  }, [selectedProjectId, pageNumber, pageItemCount]);

  const handleChangePage = (event, newPage) => {
    dispatch(setNotificationsPage(newPage + 1));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setNotificationsPageItems(parseInt(event.target.value, 10)));
    dispatch(setNotificationsPage(1));
  };

  const handleSubmit = async (body) => {
    // try {
    // } catch (error) {
    //   console.log('[CREATE PRODUCT MODAL ERROR]', error);
    // }
  };

  const handleRowClick = (notification) => {};

  return (
    <>
      <Grid container xs={12}>
        <Grid item xs={10}>
          <Breadcrumbs links={breadcrumsRoutes} currentRoute={NAMES[IDS.NOTIFICATIONS]} />
        </Grid>
      </Grid>
      <Grid container spacing={4} className={classes.main}>
        <Grid item xs={12}>
          <Widget disableWidgetMenu noBodyPadding noHeaderPadding bodyClass={classes.tableTopOverflow}>
            <Grid item xs={12}>
              <Table
                headCells={headCells}
                rows={tableData}
                count={count}
                order="asc"
                orderBy="createdAt"
                page={pageNumber - 1}
                rowsPerPage={pageItemCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onRowClick={handleRowClick}
              />
              {isPending && (
                <div className={classes.loaderWrap}>
                  <div className={classes.loader}>
                    <CircularProgress size={26} />
                  </div>
                </div>
              )}
            </Grid>
          </Widget>
        </Grid>
      </Grid>
    </>
  );
};

export default Notifications;

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    position: 'relative',
  },
  root: {
    width: '100%',
  },
  main: {
    marginTop: theme.spacing(2),
  },
  tableTopOverflow: {
    display: 'flex',
    flexDirection: 'column',
  },
  filtersLoaderWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    marginTop: theme.spacing(2),
    minHeight: theme.spacing(4),
  },
  filters: {},
  form: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
      flexDirection: 'column',
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  fieldWrap: {
    display: 'flex',
    width: '100%',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  formControl: {
    display: 'flex',
    width: '100%',
  },
  field: {
    width: '100%',
  },
  buttons: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  button: {},
  buttonReset: {
    marginLeft: theme.spacing(2),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  loaderWrap: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.overlay,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9,
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContainer: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxWidth: '70vw',
    minWidth: '70vw',
    minHeight: 400,
    maxHeight: '70vh',
    overflow: 'scroll',
  },
}));
